import styled, { css, keyframes } from 'styled-components';

import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';
import {
  CancelImg,
  CheckImg,
} from '../../../inputs/buttons/ButtonIconBackground/styles';
import { hoverCapable } from '../../../../styles/hover';

// Components

/**
 * El ancho lo manda el VISOR, no el modal.
 *
 * Como el visor toma la forma del fotograma, su ancho depende de la cámara.
 * Con `fit-content`, la barra de botones y las esquinas redondeadas siguen ese
 * ancho en vez del del modal; si no, en una ventana estrecha la barra sobresale
 * por los dos lados del vídeo.
 */
export const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
`;

export const ContainerButtons = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 10rem;
  padding: 0 ${({ theme }) => theme.spacing.xl};
  border-bottom-left-radius: 0.8rem;
  border-bottom-right-radius: 0.8rem;
  background: ${({ theme }) => theme.colors.greyVerySoft};
  box-shadow: ${({ theme }) => theme.shadows.dropdown};
  /*
   * En tablet y móvil los controles se SUPERPONEN al visor, que es lo que hacen
   * las apps de fotos: el fotograma se queda con toda la pantalla y los botones
   * flotan encima sobre un velo oscuro para que se vean sobre cualquier escena.
   * Así el visor recupera los 10rem que le comía la barra.
   */
  @media (max-width: ${({ theme }) => theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 4;
    width: auto;
    min-height: 9rem;
    padding: 0 2rem;
    border-radius: 0;
    /* El velo no es decoración: a pleno sol, el obturador blanco sobre una
       escena clara desaparece. Es lo que hacen todas las apps de cámara.
       El degradado va hasta el borde SIN parada intermedia: con una parada al
       45% el resto quedaba plano y se veía una franja gris con un corte neto. */
    background: linear-gradient(
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 0.55)
    );
    box-shadow: none;
  }
`;

export const HiddenCanvas = styled.canvas`
  display: none;
`;

/**
 * `contain` y no `cover`: lo que se encuadra es EXACTAMENTE lo que se guarda.
 * Con `cover` el visor recortaba pero la captura dibujaba el fotograma entero,
 * así que el operario veía una foto y se guardaba otra.
 */
export const PhotoPreview = styled.img`
  /* Se superpone al vídeo, que sigue vivo debajo: aceptar o rechazar vuelve al
     visor sin volver a abrir la cámara. */
  position: absolute;
  z-index: 3;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: ${({ theme }) => theme.colors.black};
`;

/**
 * El visor adopta la FORMA del fotograma real.
 *
 * Con `object-fit: contain` sobre una caja de proporción fija salían bandas
 * negras a los lados (o arriba y abajo) según la cámara. Dándole a la caja la
 * relación de aspecto del vídeo, `contain` no tiene nada que rellenar: la
 * imagen ocupa el visor entero, sin bandas y sin recortar un solo píxel — que
 * es lo que hacía `cover`, y por lo que el encuadre no coincidía con la foto.
 *
 */
export const CameraContainer = styled.div<{ $ratio?: number }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  /*
   * Manda el ANCHO, como en cualquier app de cámara: el visor se estira todo lo
   * que le dejan y el alto sale de la proporción del fotograma. El tope de alto
   * entra como límite del ancho —no como recorte— para que no aparezcan bandas
   * negras nunca: si el alto disponible no da, se estrecha el visor entero.
   *
   * En tablet y móvil se levanta el tope de 48rem: ahí sobra alto y lo que se
   * quiere es que la cámara ocupe el ancho de la pantalla, como en cualquier
   * app de fotos. El corte es el de tablet APAISADA (~1023 px) y no el de tablet
   * vertical (600 px) porque la Tab Active5 de campo mide ~960 px de CSS en
   * apaisado: con el corte pequeño se quedaba fuera justo el caso de uso real.
   */
  --visor-alto: min(48rem, calc(100vh - 22rem));
  width: min(95vw, calc(var(--visor-alto) * ${({ $ratio }) => $ratio ?? 16 / 9}));
  height: auto;
  /* Hace falta una proporción definida desde el primer render, porque es el
     visor quien le dicta el ancho a la barra de botones. El valor de partida lo
     pone el componente con la proporción recordada de la última cámara. */
  aspect-ratio: ${({ $ratio }) => $ratio ?? 16 / 9};
  /* La proporción de partida es la recordada, así que lo normal es que no haya
     cambio. Si la cámara resulta tener otra, se acompaña en vez de dar un salto
     seco. */
  transition: width 160ms ease;

  @media (max-width: ${({ theme }) => theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    /* Los controles van superpuestos, así que ya no hay que reservarles alto:
       solo el margen del propio modal. */
    --visor-alto: calc(100vh - 4rem);
  }
  /* El zoom digital escala el <video>, que si no desbordaría su caja. */
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.black};
  border-top-left-radius: 0.8rem;
  border-top-right-radius: 0.8rem;
`;

// Mismo visor negro y dimensiones que CameraContainer, pero centrando su contenido:
// muestra el loader sobre fondo negro mientras se pide permiso / arranca la cámara.
// Evita el "contenedor vacío" claro y el salto de layout (best practice getUserMedia).
export const LoadingViewport = styled(CameraContainer)`
  align-items: center;
  justify-content: center;
`;

export const Camera = styled.video<{ $zoom: number }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  /* Sin esto el navegador se queda los gestos (desplazar/zoom de página) y no
     llegan los eventos de puntero con los que se hace el pellizco.
     Ojo: nada de comillas invertidas en los comentarios de un styled-component,
     abren una plantilla anidada y el fichero deja de compilar. */
  touch-action: none;
  transform: ${({ $zoom }) => ($zoom > 1 ? `scale(${$zoom})` : 'none')};
  transform-origin: center center;
`;

const TemplateButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  ${hoverCapable} {
    &:hover {
      cursor: pointer;
      opacity: 0.8;
    }
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export const ActionButton = styled(TemplateButton)`
  position: absolute;
  right: ${({ theme }) => theme.spacing.xl};
  width: 4rem;
  height: 4rem;
  svg {
    width: 100%;
    height: 100%;
  }
  @media (max-width: ${({ theme }) => theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    right: 2rem;
    /* Sobre el velo oscuro de los controles superpuestos, el icono gris del
       cambio de cámara desaparecía. */
    svg path {
      fill: ${({ theme }) => theme.colors.white};
    }
  }
`;

const latido = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(0.9); }
`;

export const LargeActionButton = styled(TemplateButton)<{ $burst?: boolean }>`
  position: relative;
  margin: 0 auto;
  width: 7rem;
  height: 7rem;
  box-shadow: ${({ theme }) => theme.shadows.camera};
  border-radius: 100%;
  /* Sin esto, mantener pulsado en la tablet abre el menú de selección del
     navegador en vez de disparar la ráfaga. */
  touch-action: none;
  user-select: none;
  -webkit-touch-callout: none;
  /* Latido mientras dispara: es la única señal de que la ráfaga está viva. */
  ${({ $burst }) =>
    $burst &&
    css`
      animation: ${latido} 400ms ease-in-out infinite;
    `}
  svg {
    width: 100%;
    height: 100%;
  }
  @media (max-width: ${({ theme }) => theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    width: 6rem;
    height: 6rem;
  }
`;

/* Contador de la ráfaga sobre el obturador, como en las apps de cámara: es la
   señal de que cada foto se ha cogido de verdad. */
export const BurstCounter = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  font-size: 2.4rem;
  font-weight: 700;
  text-shadow: 0 0 0.6rem rgba(0, 0, 0, 0.8);
  pointer-events: none;
`;

export const ButtonLeftIconAction = styled(ButtonIconBackground)`
  align-self: initial;
  ${CheckImg} {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

export const ButtonRightIconAction = styled(ButtonIconBackground)`
  align-self: initial;
  ${CancelImg} {
    width: 2.5rem;
    height: 2.5rem;
  }
`;

/* ─── Controles superpuestos al visor ─────────────────────────────────────── */

/**
 * Base de los controles que van ENCIMA del vídeo. Fondo traslúcido oscuro
 * porque debajo hay una escena real: sin él, un botón blanco sobre nieve o
 * asfalto al sol desaparece.
 */
const OverlayControl = css`
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  color: ${({ theme }) => theme.colors.white};
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  ${hoverCapable} {
    &:hover {
      cursor: pointer;
    }
  }
`;

export const TorchToggle = styled.button<{ $active: boolean }>`
  ${OverlayControl};
  top: 1.2rem;
  left: 1.2rem;
  width: 4.4rem;
  height: 4.4rem;
  border-radius: 50%;
  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.white};
      color: ${theme.colors.black};
    `}
  svg {
    width: 2.4rem;
    height: 2.4rem;
  }
`;

/**
 * Aviso de que la PRÓXIMA foto no llevará ubicación (spec fotos
 * geolocalizadas, § 15). Arriba en el centro, entre la linterna (arriba a la
 * izquierda) y el zoom (a la derecha): no tapa ningún control y solo su
 * botón (web, «Activar ubicación») recibe toques, así que nunca frena el
 * disparo.
 */
export const LocationNotice = styled.p`
  position: absolute;
  z-index: 2;
  top: 1.2rem;
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100% - 13rem);
  margin: 0;
  padding: 0.6rem 1.2rem;
  border-radius: 2.4rem;
  background: rgba(0, 0, 0, 0.6);
  color: ${({ theme }) => theme.colors.white};
  font-family: 'Poppins', sans-serif;
  font-size: ${({ theme }) => theme.fontSize.S};
  text-align: center;
  pointer-events: none;
`;

/**
 * El único trozo del aviso que se puede tocar: vuelve a pedir la ubicación en
 * la web. Sin `:hover` (tablet-first, ver `hover.ts`).
 */
export const LocationNoticeButton = styled.button`
  pointer-events: auto;
  display: block;
  margin: 0.6rem auto 0;
  padding: 0.4rem 1.2rem;
  border: 0;
  border-radius: 2.4rem;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};
  font-family: 'Poppins', sans-serif;
  font-size: ${({ theme }) => theme.fontSize.S};
  cursor: pointer;
`;

export const ZoomBar = styled.div`
  ${OverlayControl};
  top: 50%;
  right: 1.2rem;
  transform: translateY(-50%);
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.5rem;
  border-radius: 2.4rem;
`;

export const ZoomButton = styled.button<{ $active: boolean }>`
  min-width: 4rem;
  height: 3.4rem;
  padding: 0 0.6rem;
  border: 0;
  border-radius: 1.7rem;
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  background: transparent;
  ${hoverCapable} {
    &:hover {
      cursor: pointer;
    }
  }
  ${({ $active, theme }) =>
    $active &&
    css`
      color: ${theme.colors.black};
      background: ${theme.colors.white};
    `}
`;

const focusPulse = keyframes`
  from { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
  40%  { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  to   { transform: translate(-50%, -50%) scale(1); opacity: 0; }
`;

export const FocusRingBox = styled.span<{ $x: number; $y: number }>`
  position: absolute;
  z-index: 2;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  width: 7rem;
  height: 7rem;
  border: 2px solid ${({ theme }) => theme.colors.white};
  border-radius: 0.6rem;
  pointer-events: none;
  animation: ${focusPulse} 900ms ease-out forwards;
`;

/* ─── Tira de lo capturado en esta sesión ─────────────────────────────────── */

/**
 * Se superpone al borde inferior del visor en lugar de añadirse a la barra de
 * botones: esa barra ya va justa de alto (8 rem en tablet vertical) y una fila
 * más empujaría el visor fuera de la pantalla.
 */
export const Strip = styled.div`
  position: absolute;
  z-index: 2;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.8rem 1.2rem;
  overflow-x: auto;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));

  /* Con los controles superpuestos, la tira se sube para no quedar debajo. */
  @media (max-width: ${({ theme }) => theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    bottom: 9rem;
    background: none;
  }
`;

export const StripCount = styled.span`
  flex: 0 0 auto;
  padding: 0.2rem 0.9rem;
  border-radius: 1.2rem;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  background: ${({ theme }) => theme.colors.white};
`;

export const StripItem = styled.div`
  position: relative;
  flex: 0 0 auto;
  /* Hueco para que la aspa no se salga de la tira. */
  padding: 0.6rem 0.6rem 0 0;
`;

export const StripThumb = styled.img`
  display: block;
  width: 5.6rem;
  height: 5.6rem;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.colors.white};
  border-radius: 0.4rem;
`;

/**
 * Descartar la foto recién hecha. Sin confirmación a propósito: es un gesto de
 * corrección inmediata sobre algo que se acaba de capturar, y deshacerlo es
 * simplemente volver a disparar. El área táctil se mantiene grande porque en
 * campo se usa con guantes.
 */
export const StripRemove = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.white};
  border-radius: 50%;
  font-size: 1.8rem;
  line-height: 1;
  color: ${({ theme }) => theme.colors.white};
  background: rgba(0, 0, 0, 0.75);
  ${hoverCapable} {
    &:hover {
      cursor: pointer;
      background: ${({ theme }) => theme.colors.red};
    }
  }
`;

/**
 * Cierra la ráfaga. Va a la izquierda por simetría con el botón de cambiar de
 * cámara, que ya vive pegado a la derecha.
 */
export const DoneButton = styled(TemplateButton)`
  position: absolute;
  left: ${({ theme }) => theme.spacing.xl};
  padding: 1rem 1.8rem;
  border-radius: 2rem;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.garnet};
  ${hoverCapable} {
    &:hover {
      opacity: 0.85;
    }
  }
  @media (max-width: ${({ theme }) => theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    left: 2rem;
  }
`;

/**
 * Aviso de permiso de cámara: el mismo orden y aspecto que el aviso de
 * permisos de fotos (icono, para qué sirve, pasos, la opción que hay que tocar
 * y el botón) dentro de una tarjeta blanca como la de los demás modales. Se
 * desplaza si no cabe: con el botón fuera de la pantalla no habría salida.
 */
export const PermissionNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spaces.l}rem;
  text-align: center;
  max-height: calc(100vh - 12rem);
  max-height: calc(100dvh - 12rem);
  overflow-y: auto;
  overscroll-behavior: contain;

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    gap: ${({ theme }) => theme.spaces.m}rem;
  }
`;
